// ============================================================================
// FSA NOTES HOOKS  (FSA-2.2)
// ============================================================================

import { useState, useEffect, useCallback } from 'react';
import { fsaNotesService } from 'api/services/fsa/fsaNotesService';
import type { ID } from 'api/types';
import type { NoteListItem, NoteHydrate, EngagementCustomClass } from 'api/types/fsa/noteData.types';

export const useFsaNotesList = (engagementId: ID | null | undefined) => {
  const [data, setData] = useState<NoteListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const refetch = useCallback(async () => {
    if (!engagementId) {
      setData([]);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const r = await fsaNotesService.list(engagementId);
      setData(r.data ?? []);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, [engagementId]);

  useEffect(() => { refetch(); }, [refetch]);
  return { data, loading, error, refetch };
};

/**
 * FSA-2.2 step 4b — engagement-wide custom classes for the TB classifier
 * picker. Refetches automatically on engagement change; expose `refetch`
 * for callers that need to refresh after a Note save that added/removed
 * a custom class.
 */
export const useFsaEngagementCustomClasses = (engagementId: ID | null | undefined) => {
  const [data, setData] = useState<EngagementCustomClass[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const refetch = useCallback(async () => {
    if (!engagementId) {
      setData([]);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const r = await fsaNotesService.listCustomClasses(engagementId);
      setData(r.data ?? []);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, [engagementId]);

  useEffect(() => { refetch(); }, [refetch]);
  return { data, loading, error, refetch };
};

export const useFsaNoteHydrate = (engagementId: ID | null | undefined, scheduleId: ID | null | undefined) => {
  const [data, setData] = useState<NoteHydrate | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const refetch = useCallback(async () => {
    if (!engagementId || !scheduleId) {
      setData(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const r = await fsaNotesService.hydrate(engagementId, scheduleId);
      setData(r.data ?? null);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, [engagementId, scheduleId]);

  useEffect(() => { refetch(); }, [refetch]);
  return { data, loading, error, refetch };
};
